<!-- eslint-disable max-len -->
<script setup lang="ts">
import { onUpdated, ref, watch } from 'vue';
import BtnIcon from '../BtnIcon/BtnIcon.vue';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const brokenImagePath = require('./brokenImage.png');

const IMAGE_INITIAL_SIZE = '60%'; // Update this in css class
const IMAGE_ZOOMED_SIZE = '100%';
const IMAGE_ROTATION_DEGREES = 90;

defineProps<{
	imageInfo?: string;
	imageUrl?: string;
	isPdf: boolean;
	fileType?: 'jpeg' | 'jpg' | 'png' | 'pdf';
	isLoading: boolean;
}>();

const image = ref<HTMLImageElement>();
const imageRotation = ref(0);
const imageScale = ref(IMAGE_INITIAL_SIZE);

watch(imageRotation, () => {
	image.value!.style.transformOrigin = '50% 50%';
	image.value!.style.transform = `rotate(${imageRotation.value}deg)`;
});

watch(imageScale, () => {
	image.value!.style.width = imageScale.value;
});

onUpdated(() => {
	imageRotation.value = 0;
});
</script>
<template>
	<div class="document-viewer">
		<div class="flex items-center justify-between">
			<div v-show="!isPdf" class="width-100 flex items-center justify-end gap-1">
				<BtnIcon
					icon-name="bx-zoom-in"
					ariaLabel="Zoom image in"
					:disabled="imageScale === IMAGE_ZOOMED_SIZE || !imageUrl"
					@click="imageScale = IMAGE_ZOOMED_SIZE"
				/>
				<BtnIcon
					icon-name="bx-zoom-out"
					ariaLabel="Zoom image out"
					:disabled="imageScale === IMAGE_INITIAL_SIZE || !imageUrl"
					@click="imageScale = IMAGE_INITIAL_SIZE"
				/>
				<BtnIcon
					icon-name="bx-rotate-right"
					ariaLabel="Rotate image right"
					:disabled="!imageUrl"
					@click="imageRotation += IMAGE_ROTATION_DEGREES"
				/>
			</div>
		</div>
		<div class="document-viewer__img-container" tabindex="0">
			<object
				v-if="imageUrl && isPdf"
				ref="image"
				:data="imageUrl"
				class="mt-2"
				width="100%"
				height="100%"
				type="application/pdf"
				role="document"
				title="PDF document"
				aria-label="PDF document"
			>
				PDF display
			</object>
			<p v-else-if="isLoading" class="img-loader width-60 height-50 rounded-1" />
			<img
				v-else-if="imageUrl && !isPdf"
				ref="image"
				class="document-viewer__img-container__img"
				no-referrer
				:onerror="`this.onerror=null;this.src='${brokenImagePath}';`"
				:src="imageUrl"
				alt="Image of document"
			/>
		</div>
	</div>
</template>
<style lang="scss" scoped>
.document-viewer {
	border-radius: $border-radius-2;
	background-color: $color-gray-100;
	padding: $spacing-2;

	&__img-container {
		width: 100%;
		height: max(80vh, 780px);
		overflow: scroll;
		display: flex;
		align-items: center;
		justify-content: center;

		&::-webkit-scrollbar {
			display: none;
		}

		&__img {
			width: 60%;
			height: auto;
			border: none;
			border-radius: $border-radius-2;
			margin: 0 auto;
			background-color: $color-gray-200;
			min-width: 200px;
			min-height: 200px;
			text-align: center;
		}
	}
}
</style>
